import { Loader2 } from "lucide-react";
import { formatAddressLines, mailToLabel, VERIFICATION_LABEL, type Address, type AddressKind } from "@shared/address";
import { useCompanyAddresses, type AddressInput } from "@/hooks/useCompanyAddresses";
import { cn } from "@/lib/utils";
import { RecordPanel } from "./RecordFrame";
import { CopyButton } from "./ReachItem";
import { AddressEditor } from "./AddressEditor";
import { fullDayOfYmd } from "./recordFormat";

const VERIFICATION_TONE = {
  county_records: "text-emerald-700 dark:text-emerald-400",
  provided: "text-muted-foreground",
  county_mismatch: "text-amber-700 dark:text-amber-400",
} as const;

/**
 * The company's mailing address on every company page. Prospects and clients
 * also carry the owner's home and a "Packets go to" line: a packet goes home
 * only when county records confirmed that address.
 */
export function RailAddresses({ companyId, defaultAddressee }: { companyId: string; defaultAddressee: string | null }) {
  const { data, isLoading, isError, refetch, save, remove } = useCompanyAddresses(companyId);
  return (
    <RecordPanel title="Addresses">
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : isError || !data ? (
        <p className="text-[12.5px] text-muted-foreground">
          Couldn't load addresses.{" "}
          <button type="button" onClick={() => refetch()} className="font-semibold text-primary-text hover:underline">Retry</button>
        </p>
      ) : (
        <div className="grid gap-2.5 text-[12.5px]">
          {data.allowsOwnerHome && data.mailTo && (
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="text-muted-foreground">Packets go to</span>
              <span className={cn("text-right font-semibold", data.mailTo.to === null && "text-amber-700 dark:text-amber-400")}>
                {mailToLabel(data.mailTo)}
              </span>
            </div>
          )}
          <AddressBlock
            kind="company" label="Company address" value={data.company} defaultAddressee={defaultAddressee}
            onSave={(input) => save("company", input)} onRemove={() => remove("company")}
          />
          {data.allowsOwnerHome && (
            <AddressBlock
              kind="owner_home" label="Owner's home" value={data.ownerHome} defaultAddressee={defaultAddressee}
              onSave={(input) => save("owner_home", input)} onRemove={() => remove("owner_home")}
            />
          )}
        </div>
      )}
    </RecordPanel>
  );
}

function AddressBlock({ kind, label, value, defaultAddressee, onSave, onRemove }: {
  kind: AddressKind; label: string; value: Address | null; defaultAddressee: string | null;
  onSave: (input: AddressInput) => Promise<unknown>; onRemove: () => Promise<unknown>;
}) {
  const editor = (trigger: string, aria: string) => (
    <AddressEditor kind={kind} value={value} defaultAddressee={defaultAddressee} onSave={onSave} onRemove={onRemove}>
      <button type="button" aria-label={aria} className="text-xs font-semibold text-primary-text hover:underline">{trigger}</button>
    </AddressEditor>
  );

  if (!value) {
    return (
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground">{label}</span>
        {editor("Add", `Add ${label.toLowerCase()}`)}
      </div>
    );
  }

  const lines = formatAddressLines(value, { addressee: kind === "owner_home" ? value.addressee : null });
  const v = kind === "owner_home" ? value.verification : undefined;
  return (
    <div className="grid gap-0.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">{label}</span>
        <span className="flex items-center gap-1.5">
          <CopyButton value={lines.join("\n")} label={label.toLowerCase()} />
          {editor("Edit", `Edit ${label.toLowerCase()}`)}
        </span>
      </div>
      <address className="select-all not-italic [overflow-wrap:anywhere]">
        {lines.map((line, i) => <div key={i}>{line}</div>)}
      </address>
      {v && (
        <div className={cn("text-xs", VERIFICATION_TONE[v])} title={value.verificationNote ?? undefined}>
          {VERIFICATION_LABEL[v]}
          {value.verifiedOn && ` · ${fullDayOfYmd(value.verifiedOn)}`}
        </div>
      )}
    </div>
  );
}
